/**
 * TestIt Service (BL-214).
 *
 * Client for the reusable test store under /api/heroes/testit/:talentId/*.
 * Mirrors the backend `TalentTestResultView` shape (validity is backend-computed
 * — the FE never does date math).
 */

import type { LanguageTestFailureReason, LanguageTestLanguage } from '@tonnie/heroes';

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface TalentTestResultView {
  id: string;
  testId: string;
  subTest: string;
  /** i18n key for the test's display name. */
  labelKey: string;
  /** The widget that administers this test. */
  widgetId: string;
  /** The language a language test is in (ISO 639-1, `es`); `null` for any other test. */
  languageCode: string | null;
  status: string;
  result: Record<string, unknown> | null;
  score: string | null;
  source: string;
  invitationId: string | null;
  takenAt: string;
  expiresAt: string | null;
  /** Backend-computed validity (the clock). */
  isValid: boolean;
  /**
   * Backend-computed: may this stand in place of TAKING the test again.
   * Narrower than `isValid` — a self-declared level is valid but not reusable.
   */
  isReusable: boolean;
  /** Whole days until expiry (null = never expires, 0 = expired). */
  daysRemaining: number | null;
  /** The unit's active result — every other attempt in a history is superseded. */
  isLatest: boolean;
}

/** One invited test in the citizen's TestIt assignment. */
export interface InvitedTest {
  testId: string;
  labelKey: string;
  widgetId: string;
  /** A still-valid banked result exists for this test. */
  done: boolean;
  /** When the result that satisfies it was taken (ISO); null until done. */
  takenAt: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
}

/** The citizen's current invited-TestIt assignment (BL-214). */
export interface InvitedTestitAssignment {
  /** True when the caller has an accepted `testit` invitation with chosen tests. */
  invited: boolean;
  journeyId: string | null;
  tests: InvitedTest[];
  /** True when every invited test has a still-valid banked result. */
  completed: boolean;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class TestItService {
  constructor(private readonly client: TonnieClient) {}

  /** The caller's invited-TestIt assignment (which specific tests + done status). */
  getInvitation(): Promise<InvitedTestitAssignment> {
    return this.client.get<InvitedTestitAssignment>(API_ENDPOINTS.TESTIT.INVITATION);
  }

  /** All active (latest) results for the talent. */
  getResults(talentId: string): Promise<TalentTestResultView[]> {
    return this.client.get<TalentTestResultView[]>(API_ENDPOINTS.TESTIT.RESULTS(talentId));
  }

  /** Every attempt at every test (newest first) — the TestIt page's History tab. */
  getAllHistory(talentId: string): Promise<TalentTestResultView[]> {
    return this.client.get<TalentTestResultView[]>(API_ENDPOINTS.TESTIT.HISTORY(talentId));
  }

  /** Full history for one test unit (newest first). */
  getResultHistory(
    talentId: string,
    testId: string,
    subTest?: string
  ): Promise<TalentTestResultView[]> {
    const base = API_ENDPOINTS.TESTIT.RESULT_HISTORY(talentId, testId);
    const path = subTest ? `${base}?subTest=${encodeURIComponent(subTest)}` : base;
    return this.client.get<TalentTestResultView[]>(path);
  }

  /** Soft-delete a result; the next most-recent one is promoted to latest. */
  deleteResult(talentId: string, id: string): Promise<{ deleted: boolean }> {
    return this.client.delete<{ deleted: boolean }>(API_ENDPOINTS.TESTIT.RESULT(talentId, id));
  }

  /**
   * Standalone submit (P2b): record a self-taken result from the catalog (no
   * CheckUp assessment). Ownership + catalog validation happen server-side.
   */
  recordStandalone(
    talentId: string,
    dto: {
      testId: string;
      subTest?: string;
      result?: Record<string, unknown> | null;
      score?: string | null;
    }
  ): Promise<TalentTestResultView | { recorded: false }> {
    return this.client.post<TalentTestResultView | { recorded: false }>(
      API_ENDPOINTS.TESTIT.RESULTS(talentId),
      dto
    );
  }

  // ── Business (Vetting) ───────────────────────────────────────────────────

  /**
   * A candidate's active results, read for the business. Pipeline-gated cross-
   * tenant read — distinct from `getResults`, which is the citizen self-read and
   * returns [] for a business caller.
   */
  getResultsForBusiness(talentId: string, journeyId: string): Promise<TalentTestResultView[]> {
    return this.client.get<TalentTestResultView[]>(API_ENDPOINTS.TESTIT.VETTING_RESULTS(talentId), {
      params: { journeyId },
    });
  }

  /**
   * Full TestIt overview for the Vetting tab: invited tests (with done status) +
   * rich results + completion, in one pipeline-gated cross-tenant call.
   */
  getOverviewForBusiness(talentId: string, journeyId: string): Promise<TestitVettingOverview> {
    return this.client.get<TestitVettingOverview>(API_ENDPOINTS.TESTIT.VETTING_OVERVIEW(talentId), {
      params: { journeyId },
    });
  }

  /**
   * The language certificates whose latest analysis produced no level, with the
   * typed reason — pipeline-gated and scoped like the overview (W5.6).
   */
  getFailedAttemptsForBusiness(
    talentId: string,
    journeyId: string
  ): Promise<TestitFailedAttempt[]> {
    return this.client.get<TestitFailedAttempt[]>(
      API_ENDPOINTS.TESTIT.VETTING_FAILED_ATTEMPTS(talentId),
      { params: { journeyId } }
    );
  }

  /** Presigned pitch playback URL for the Vetting detail sheet. */
  getPitchUrlForBusiness(
    talentId: string,
    journeyId: string
  ): Promise<{
    playbackUrl: string | null;
    recordedAt: string | null;
  }> {
    return this.client.get(API_ENDPOINTS.TESTIT.VETTING_PITCH_URL(talentId), {
      params: { journeyId },
    });
  }

  /** Per-test attempt history for the Vetting detail sheet. */
  getResultHistoryForBusiness(
    talentId: string,
    journeyId: string,
    testId: string,
    subTest?: string
  ): Promise<TalentTestResultView[]> {
    return this.client.get<TalentTestResultView[]>(
      API_ENDPOINTS.TESTIT.VETTING_RESULT_HISTORY(talentId, testId),
      { params: { journeyId, ...(subTest ? { subTest } : {}) } }
    );
  }

  /** Nudge the candidate to complete their TestIt. Throws 429 if sent too recently. */
  remind(talentId: string, journeyId: string): Promise<{ reminded: boolean }> {
    return this.client.post<{ reminded: boolean }>(
      API_ENDPOINTS.TESTIT.VETTING_REMIND(talentId),
      undefined,
      { params: { journeyId } }
    );
  }

  /**
   * Record (or replace) a Pass/Decline verdict for a talent.
   *
   * No `invitationId`: the engagement is resolved server-side from the caller's
   * own pipeline (W3.8). No `snapshot` either (W5.8): the evidence is captured
   * server-side in the verdict's own transaction — read it through
   * `EngagementEvidenceService.getVerdictHistory` / `getCapture`. The endpoint's
   * schema is strict, so sending either is a 400 rather than a field silently
   * dropped.
   */
  recordVerdict(
    talentId: string,
    dto: { journeyId: string; verdict: 'passed' | 'declined' }
  ): Promise<TestitVerdictView> {
    return this.client.post<TestitVerdictView>(API_ENDPOINTS.TESTIT.VERDICT(talentId), dto);
  }
}

export interface TestitVerdictView {
  id: string;
  talentId: string;
  /** The engagement this decision was made under (W3.8). */
  journeyInstanceId: string | null;
  /** LEGACY — always null; superseded by `journeyInstanceId`. */
  invitationId: string | null;
  verdict: 'passed' | 'declined';
  /**
   * LEGACY mirror of the server's TestIt read at decision time
   * (`{ results: [{ testId, score, isValid }] }`), kept for the previous
   * release's "since expired" badge. The record of the decision is its
   * evidence capture — see `EngagementEvidenceService`.
   */
  snapshot: Record<string, unknown> | null;
  decidedByUserId: string | null;
  decidedAt: string;
}

/** One invited test in the business-side TestIt overview. */
export interface BusinessInvitedTest {
  testId: string;
  labelKey: string;
  widgetId: string;
  /** A still-valid banked result exists for this test. */
  done: boolean;
  expiresAt: string | null;
  daysRemaining: number | null;
}

/** A language test whose latest certificate was refused, and why (W5.6). */
export interface TestitFailedAttempt {
  testId: string;
  labelKey: string;
  widgetId: string;
  language: LanguageTestLanguage;
  reason: LanguageTestFailureReason;
  attemptedAt: string;
}

/** Business-side TestIt overview: invited set (done status) + rich results. */
export interface TestitBusinessOverview {
  /**
   * Has the citizen granted this company the `testit` consent scope? (W8.D)
   *
   * DISTINCT from `invited`, and the distinction is the whole point: `invited`
   * is what the COMPANY asked for, `entitled` is what the CITIZEN still
   * permits. TestIt's own invitation-derived gate never asked the consent
   * model anything, so a citizen who revoked kept every banked result readable
   * by a company that had once invited them.
   *
   * `false` ⇒ `invitedTests` keeps its shape (the company's own ask is its own
   * record, the same rule the TestIt VERDICT follows) while `results` is `[]`
   * and `completed` is `false` BECAUSE of the revocation. Every entry's `done`
   * is `false` for the same reason — whether a candidate COMPLETED a test is a
   * fact about them, and a withheld read may not state it either way. Render
   * "withheld" per row, never "Pending"/"Not taken", and never a `0/3` ring.
   */
  entitled: boolean;
  invited: boolean;
  invitationKind: 'full_checkup' | 'testit';
  invitedTests: BusinessInvitedTest[];
  results: TalentTestResultView[];
  completed: boolean;
}

/**
 * What the Vetting tab may do on this relationship — the server's answer. A
 * TestIt invitation takes a Pass/Decline, a reminder and a re-ask; a CheckUp
 * relationship is a read-only repository of what the citizen banked. Verdict
 * and reminder stay admin actions: hide them from a Team Member too.
 */
export interface TestitVettingActions {
  recordVerdict: boolean;
  remind: boolean;
  askForAssessment: boolean;
}

/** One test of the catalog the Vetting tab lists for a CheckUp relationship. */
export interface BusinessCatalogTest {
  testId: string;
  labelKey: string;
  widgetId: string;
  /** The language a language test is in (ISO 639-1); `null` for any other test. */
  languageCode: string | null;
}

/**
 * The Vetting tab's read: the overview plus what the tab may do, and the test
 * catalog it lists for a CheckUp relationship (neither is evidence).
 */
export interface TestitVettingOverview extends TestitBusinessOverview {
  actions: TestitVettingActions;
  /**
   * D2 — the company's request for more on this relationship, waiting on the
   * talent ("Asked for the CheckUp — waiting for the talent"). Until they
   * answer, the tab stays on the ask they accepted.
   */
  pendingRequest: {
    asks: 'checkup' | 'tests';
    /** The tests it asks for (`tests` only). */
    tests: string[];
    askedAt: string;
  } | null;
  /** The B2B catalog, then any other language a result is in — each listed taken or not. */
  catalogTests: BusinessCatalogTest[];
}

export function createTestItService(client: TonnieClient): TestItService {
  return new TestItService(client);
}
